/**
 * Recharts wrappers.
 *
 * Colours are read from the CSS custom properties at render time so charts
 * follow the active theme; `useTheme().resolved` is a dependency so they
 * recompute when the theme flips.
 */
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import { useTheme } from '../lib/theme'
import { relabelText, usePartnerTerm } from '../lib/terms'

const readVar = (name, fallback) => {
  if (typeof window === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

export function usePalette() {
  const { resolved, scheme } = useTheme()
  return useMemo(
    () => ({
      series: [1, 2, 3, 4, 5, 6].map((i) => readVar(`--chart-${i}`, '#0b7a55')),
      grid: readVar('--chart-grid', '#e6e9ef'),
      fg: readVar('--fg-muted', '#5b6472'),
      subtle: readVar('--fg-subtle', '#656e7c'),
      accent: readVar('--accent', '#0b7a55'),
      ok: readVar('--ok', '#17784b'),
      warn: readVar('--warn', '#9a5b06'),
      danger: readVar('--danger', '#c02626'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolved, scheme]
  )
}

const Tip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="chart-tip">
      <div className="chart-tip-label">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="row row-tight">
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
          <span className="muted">{p.name}</span>
          <strong className="nums">{Number(p.value).toLocaleString()}</strong>
          {unit && <span className="subtle">{unit}</span>}
        </div>
      ))}
    </div>
  )
}

const AXIS = { tickLine: false, axisLine: false, tick: { fontSize: 11 } }

/* Legends list the series in the order the chart draws them. Recharts 3 sorts them by
   label instead, which put "Distinct partner units" before "Updates" beside bars drawn
   the other way round. A sort key rather than `null`, which Recharts warns can reorder
   between renders. */
const inSeriesOrder = (series) => (item) => series.findIndex((s) => s.key === item.dataKey)

/**
 * `xTicks` and `xFormat` are for series plotted finer than they need labelling —
 * monthly points a caller wants marked once a year, say. The tooltip still reports
 * the point's own value; only the axis is thinned.
 */
export function TrendChart({ data, x, series, height = 240, area, unit, stacked, xTicks, xFormat }) {
  // A series name is a legend and a tooltip, both written by the page that draws the
  // chart, so both follow the installation's term. See lib/terms.jsx.
  const term = usePartnerTerm()
  const p = usePalette()
  const Chart = area ? AreaChart : LineChart
  return (
    <ResponsiveContainer width="100%" height={height}>
      <Chart data={data} margin={{ top: 6, right: 10, left: -18, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={p.grid} />
        <XAxis dataKey={x} {...AXIS} minTickGap={22} ticks={xTicks} tickFormatter={xFormat} />
        <YAxis {...AXIS} width={54} />
        <Tooltip content={<Tip unit={unit} />} />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={inSeriesOrder(series)} />}
        {series.map((s, i) =>
          area ? (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={relabelText(s.label, term)}
              stroke={s.color ?? p.series[i % p.series.length]}
              fill={s.color ?? p.series[i % p.series.length]}
              fillOpacity={0.16}
              strokeWidth={2}
              stackId={stacked ? '1' : undefined}
            />
          ) : (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={relabelText(s.label, term)}
              stroke={s.color ?? p.series[i % p.series.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          )
        )}
      </Chart>
    </ResponsiveContainer>
  )
}

/**
 * `onPick` makes the chart a control, called with the category picked and the series it
 * was picked in: `onPick(x, seriesKey)`.
 *
 * What counts as one target depends on how many series there are. With one, the whole
 * column is clickable rather than the drawn bar, so a thin bar is no harder to hit than
 * a tall one and a year with two updates is as clickable as a year with twenty. With
 * several, each drawn segment is its own target — on a stacked chart the segment is the
 * answer, since "dormant in Uzbekistan" and "never wrote in Uzbekistan" are two
 * different questions in the same column.
 *
 * `activeX` and `activeKey` say what is picked, and everything else fades so the chart
 * shows what the page around it is filtered to. Leave `activeKey` off to light up a
 * whole column.
 *
 * A click on an SVG is a mouse-only affordance, so a caller offering this must also
 * offer the same choice as a real control — a select, or the pressable tiles over the
 * coverage report's chart.
 */
export function BarsChart({
  data, x, series, height = 240, horizontal, unit, stacked, onPick, activeX, activeKey,
}) {
  const p = usePalette()
  const term = usePartnerTerm()
  const picked = activeX === null || activeX === undefined ? null : String(activeX)
  // One series: the column is the target, and there is only one series it can mean.
  // Several: each Bar takes its own handler below, and a chart-level one here would
  // fire alongside it and overwrite the segment with the column.
  const wholeColumn = onPick && series.length === 1
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 6, right: 12, left: horizontal ? 8 : -18, bottom: 0 }}
        onClick={
          wholeColumn
            ? (state) => state?.activeLabel != null && onPick(String(state.activeLabel), series[0].key)
            : undefined
        }
        style={onPick ? { cursor: 'pointer' } : undefined}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={horizontal} horizontal={!horizontal} stroke={p.grid} />
        {horizontal ? (
          <>
            <XAxis type="number" {...AXIS} />
            {/* interval={0} stops recharts dropping every other category label */}
            <YAxis type="category" dataKey={x} {...AXIS} width={150} interval={0} />
          </>
        ) : (
          <>
            <XAxis dataKey={x} {...AXIS} minTickGap={14} />
            <YAxis {...AXIS} width={54} />
          </>
        )}
        <Tooltip content={<Tip unit={unit} />} cursor={{ fill: p.grid, opacity: 0.35 }} />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={inSeriesOrder(series)} />}
        {series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={relabelText(s.label, term)}
            fill={s.color ?? p.series[i % p.series.length]}
            radius={horizontal ? [0, 3, 3, 0] : [3, 3, 0, 0]}
            stackId={stacked ? '1' : undefined}
            maxBarSize={38}
            onClick={
              onPick && !wholeColumn
                ? (bar) => bar?.payload?.[x] != null && onPick(String(bar.payload[x]), s.key)
                : undefined
            }
          >
            {/* Only while something is picked: a Cell per datum is how one bar can be
                told apart from its neighbours, and with nothing picked there is nothing
                to tell apart. A picked series narrows it further — the other segments of
                the same column fade with the other columns. */}
            {picked !== null
              ? data.map((d, j) => (
                  <Cell
                    key={j}
                    fillOpacity={
                      String(d[x]) === picked && (activeKey == null || activeKey === s.key) ? 1 : 0.28
                    }
                  />
                ))
              : null}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}

/**
 * `onPick` is called with the name of the slice clicked, and `activeName` fades the
 * others — the same bargain BarsChart makes, so a page carrying both can put them on one
 * selection. A slice is the only target here: a donut has no column of empty space to
 * widen it with, which is worth knowing for the thin ones.
 */
export function DonutChart({
  data, nameKey = 'name', valueKey = 'value', height = 220, colors, onPick, activeName,
}) {
  const p = usePalette()
  const picked = activeName === null || activeName === undefined ? null : String(activeName)
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={nameKey}
          innerRadius="55%"
          outerRadius="82%"
          paddingAngle={2}
          stroke="none"
          onClick={
            onPick
              ? (slice) => {
                  // The row is spread onto the slice and also kept whole as `payload`.
                  const name = slice?.payload?.[nameKey] ?? slice?.[nameKey]
                  if (name != null) onPick(String(name))
                }
              : undefined
          }
          style={onPick ? { cursor: 'pointer' } : undefined}
        >
          {data.map((d, i) => (
            <Cell
              key={i}
              fill={colors?.[i] ?? p.series[i % p.series.length]}
              fillOpacity={picked === null || String(d[nameKey]) === picked ? 1 : 0.28}
            />
          ))}
        </Pie>
        <Tooltip content={<Tip />} />
        {/* Slices in the order the data gives them, as the donut draws them. */}
        <Legend
          wrapperStyle={{ fontSize: 12 }}
          itemSorter={(item) => data.findIndex((d) => String(d[nameKey]) === String(item.value))}
        />
      </PieChart>
    </ResponsiveContainer>
  )
}

/**
 * Year x month grid of newsletter counts. Cheap to render and reads better than
 * a line chart for spotting seasonal gaps in a 21-year archive.
 *
 * Cells are wide and short rather than square: with a row per year, square cells on a
 * wide screen made each one 86px and the card 2,000px tall. Colour carries the reading
 * here, not area, so the height is fixed in CSS and only the width flexes.
 *
 * `hrefForMonth` is optional and takes a `YYYY-MM` key. Given one, every month that
 * has updates becomes a link to it; months with none stay inert, since there is
 * nothing on the other side of that click.
 */
/**
 * The same twelve months, one line per year, for reading this year against the last two.
 *
 * Every point and every year is a link. That is the reason this is its own component
 * rather than a `TrendChart` with three series: a trend line is a picture, and the ask
 * here was for a way in — pick March and see March's updates.
 *
 * THE DOTS ARE REAL LINKS, SVG anchors with an `href`, not click handlers painted on a
 * shape. A click on an SVG is a mouse-only affordance and this file's own rule is that
 * anything offering one must offer a real control beside it; the honest way to keep that
 * rule is for the affordance to *be* the control. So each dot is focusable, has an
 * accessible name that reads "March 2026: 24 updates", and can be middle-clicked or
 * copied like any other link. Thirty-six of them is the same count MonthHeatmap below
 * already puts in the tab order, and for the same reason.
 *
 * The legend is links too, and sits above the plot rather than under it — a legend that
 * is also navigation belongs where a reader looks first.
 *
 * `data` is one row per month: `{ label: 'Jan', full: 'March', month: '03', 2024: n, … }`.
 * `years` are the keys, oldest first; the last is drawn heaviest, being now.
 */
/*
 * How the years are told apart, in one place because two components draw them: the plot
 * and the legend, which live in different parts of the card and must not drift.
 *
 * The years behind us in grey and this one in the accent: history is context and now is
 * the reading, so only one line here is worth a colour. Three saturated hues out of the
 * chart palette said the opposite — that all three mattered equally and loudly. The greys
 * are the two foreground tokens, so each is quieter than the other against whichever
 * background the theme paints, and the oldest is the quieter of them in both.
 *
 * And the oldest is dashed, which is not decoration. Measured, the two greys sit 1.9:1
 * apart in either theme — enough to see, not enough to be sure of, and nothing at all to
 * a reader who cannot separate two greys. The dash survives any of that, and reads as
 * "further away" besides. So nothing rests on colour alone: this year is the only line
 * with a hue, and it is also the heaviest.
 */
const yearColour = (p, i) => [p.subtle, p.fg, p.accent][i] ?? p.series[i % p.series.length]
const yearDash = (i) => (i === 0 ? '5 4' : undefined)

/**
 * The years, as links, for the card's own header.
 *
 * Its own component because it belongs beside the card's title rather than above the
 * plot: a legend that is also navigation is looked for up there, and the band it used to
 * occupy was most of the empty space between the title and the chart.
 */
export function YearOverYearLegend({ years, hrefForYear, unit = 'updates' }) {
  const p = usePalette()
  return (
    <div className="yoy-legend">
      {years.map((y, i) => {
        /* A line rather than a dot, so a dashed series is dashed here too — a round
           swatch would say the two grey years differ only in shade, which is the thing
           the dash is there to stop. */
        const colour = yearColour(p, i)
        const swatch = (
          <span
            className="yoy-swatch"
            style={
              yearDash(i)
                ? { background: `repeating-linear-gradient(90deg, ${colour} 0 5px, transparent 5px 9px)` }
                : { background: colour }
            }
            aria-hidden="true"
          />
        )
        return hrefForYear ? (
          <Link key={y} className="yoy-legend-item" to={hrefForYear(y)} title={`Every ${unit} from ${y}`}>
            {swatch}
            <span className="nums">{y}</span>
          </Link>
        ) : (
          <span key={y} className="yoy-legend-item">
            {swatch}
            <span className="nums">{y}</span>
          </span>
        )
      })}
    </div>
  )
}

export function YearOverYearChart({ data, years, height = 110, hrefForPoint, unit = 'updates' }) {
  const p = usePalette()
  const navigate = useNavigate()
  const colourFor = (i) => yearColour(p, i)

  /* One dot, as a link. Recharts calls this per point with the point's centre and its
     row, and renders the results as a list — hence the key. */
  const dotFor = (year, colour) => {
    const Dot = ({ cx, cy, payload, index }) => {
      // A month the year has not reached yet carries null rather than nought — see the
      // note where this chart's rows are built — and null has no point to draw.
      if (cx == null || cy == null || payload?.[year] == null) return null
      const n = payload[year]
      const to = hrefForPoint?.(year, payload?.month)
      const one = unit.replace(/s$/, '')
      const name = `${payload?.full ?? payload?.label} ${year}: ${n.toLocaleString()} ${n === 1 ? one : unit}`
      const mark = (
        <circle className="yoy-dot-mark" cx={cx} cy={cy} r={4} fill={colour} stroke={colour} strokeWidth={2} />
      )
      if (!to) return <g key={`${year}-${index}`}>{mark}</g>
      return (
        <a
          key={`${year}-${index}`}
          className="yoy-dot"
          href={to}
          aria-label={name}
          /* The href stays a real URL, so a middle-click opens a tab and the address can
             be copied; an ordinary click is turned into a client-side move instead. */
          onClick={(e) => {
            e.preventDefault()
            navigate(to)
          }}
        >
          <title>{name}</title>
          {mark}
        </a>
      )
    }
    Dot.displayName = `YearOverYearDot(${year})`
    return Dot
  }

  return (
    <div className="yoy">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={p.grid} />
          <XAxis dataKey="label" {...AXIS} />
          <YAxis {...AXIS} width={54} allowDecimals={false} />
          <Tooltip content={<Tip unit={unit} />} />
          {years.map((y, i) => (
            <Line
              key={y}
              type="monotone"
              dataKey={y}
              name={y}
              stroke={colourFor(i)}
              strokeWidth={i === years.length - 1 ? 2.5 : 1.75}
              strokeDasharray={yearDash(i)}
              dot={dotFor(y, colourFor(i))}
              /* No activeDot, and this is the reason rather than a preference. Recharts
                 draws it in a layer above the line, so hovering a point put a plain circle
                 over the anchor and the click landed on that — every month link was dead
                 to an actual pointer while working perfectly when dispatched at the
                 element. What it was there for, saying which point you are on, the dot's
                 own hover already does. */
              activeDot={false}
              /* Off, because the dots are links: an animating line moves its own targets
                 out from under the pointer on the way in. */
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

export function MonthHeatmap({ byMonth, hrefForMonth }) {
  const p = usePalette()
  const { years, matrix, max } = useMemo(() => {
    const m = new Map()
    let max = 0
    for (const row of byMonth ?? []) {
      const [y, mo] = String(row.month).split('-')
      if (!y || !mo) continue
      if (!m.has(y)) m.set(y, Array(12).fill(0))
      m.get(y)[Number(mo) - 1] = row.n
      if (row.n > max) max = row.n
    }
    return { years: [...m.keys()].sort().reverse(), matrix: m, max: max || 1 }
  }, [byMonth])

  const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']

  return (
    <div className="heatmap">
      {/* Row height, month labels and cells all come from .heatmap in styles.css — the
          rows are what set this card's height, and a square cell stretched to the
          column width made it 2,000px tall on a wide screen. */}
      <div className="heatmap-grid">
        <span />
        {MONTHS.map((mo, i) => (
          <span key={i} className="tiny subtle center">
            {mo}
          </span>
        ))}
        {years.map((y) => (
          <div key={y} style={{ display: 'contents' }}>
            <span className="tiny subtle nums heatmap-year">{y}</span>
            {matrix.get(y).map((n, i) => {
              const month = `${y}-${String(i + 1).padStart(2, '0')}`
              const label = `${month}: ${n} update${n === 1 ? '' : 's'}`
              const style = n
                ? { background: p.accent, opacity: 0.18 + 0.82 * Math.sqrt(n / max) }
                : undefined
              // The title carries the count on hover either way; a link also needs the
              // same text as its accessible name, since the cell has no content to read.
              return hrefForMonth && n ? (
                <Link
                  key={i}
                  className="heat-cell heat-cell-link"
                  to={hrefForMonth(month)}
                  title={label}
                  aria-label={label}
                  style={style}
                />
              ) : (
                <div key={i} className="heat-cell" title={label} style={style} />
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
